import { useEffect, useState } from 'react';

/*
 * Mise à jour de l'application installée (recopié dans l'application moniteur : corriger dans les deux).
 *
 * Installée sur l'écran d'accueil, l'application est remise au premier plan telle que le téléphone l'a
 * laissée, avec le code chargé avant la livraison : sans rechargement, rien ne change. Au retour au
 * premier plan, et tous les quarts d'heure tant qu'elle reste affichée, elle relit la version publiée
 * (/version.json, voir vite-plugins/buildVersion.ts) ; si elle diffère de la sienne, le bandeau
 * « Mettre à jour » s'affiche (UpdateAppBanner).
 */

const CHECK_INTERVAL_MS = 15 * 60_000;

/** Version de build de la page affichée ; absente en développement (aucune vérification). */
export function currentVersion(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="permio-version"]')?.content || null;
}

/** Version publiée sur le serveur, lue sans cache ; null si illisible (hors ligne, build sans version). */
export async function publishedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json', { cache: 'no-store' });
    if (!res.ok) return null;
    const { version } = (await res.json()) as { version?: unknown };
    return typeof version === 'string' && version ? version : null;
  } catch {
    return null;
  }
}

/**
 * Enregistre le service worker (PWA installable + offline + push). Son adresse porte la version de la
 * page : à chaque livraison, le navigateur installe le nouveau service worker, qui vide l'ancien cache.
 */
export function registerServiceWorker(): void {
  const version = currentVersion();
  navigator.serviceWorker.register(version ? `/sw.js?v=${encodeURIComponent(version)}` : '/sw.js').catch(() => {
    // best-effort : l'app fonctionne sans SW.
  });
}

/** Vrai dès que la version publiée n'est plus celle de la page affichée. */
export function useNewVersionAvailable(): boolean {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    const version = currentVersion();
    if (!version || available) return;
    let cancelled = false;
    const check = async () => {
      if (document.visibilityState === 'hidden') return;
      const published = await publishedVersion();
      if (!cancelled && published && published !== version) setAvailable(true);
    };
    const onCheck = () => void check();
    document.addEventListener('visibilitychange', onCheck);
    window.addEventListener('online', onCheck);
    const timer = window.setInterval(onCheck, CHECK_INTERVAL_MS);
    onCheck();
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onCheck);
      window.removeEventListener('online', onCheck);
      window.clearInterval(timer);
    };
  }, [available]);

  return available;
}
